import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Sparkles, ArrowRight } from "lucide-react";

export default function Login() {
  const { login, loading } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");

  const submit = async (e) => {
    e.preventDefault();
    try {
      const u = await login(email, password);
      toast.success("Bem-vindo de volta!");
      navigate(u.is_admin ? "/admin/dashboard" : "/app/dashboard");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Falha ao entrar");
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2">
      <div className="hidden lg:flex relative items-center justify-center p-12 overflow-hidden border-r border-border">
        <div className="absolute inset-0 grid-bg opacity-50" />
        <div className="absolute inset-0 bg-gradient-to-br from-primary/10 via-transparent to-transparent" />
        <div className="relative max-w-md fade-up">
          <div className="flex items-center gap-2 mb-10">
            <div className="w-8 h-8 rounded-md bg-primary flex items-center justify-center">
              <Sparkles className="w-4 h-4 text-white" />
            </div>
            <span className="font-display font-bold text-xl">SmartStockAI</span>
          </div>
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-3">Inteligência preditiva</p>
          <h1 className="font-display text-5xl font-bold leading-tight tracking-tight">
            O futuro do seu estoque, hoje.
          </h1>
          <p className="text-muted-foreground mt-6 leading-relaxed">
            Antecipe rupturas, libere capital parado e tome decisões mais inteligentes com a IA da SmartStockAI.
          </p>
          <div className="mt-12 grid grid-cols-3 gap-4">
            {[
              ["94%", "precisão"],
              ["12d", "antecipação"],
              ["+38%", "margem"],
            ].map(([v, l]) => (
              <div key={l} className="p-4 rounded-lg border border-border bg-card/50">
                <div className="font-display text-2xl font-bold text-primary">{v}</div>
                <div className="text-xs text-muted-foreground mt-1">{l}</div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 sm:p-12">
        <form data-testid="login-form" onSubmit={submit} className="w-full max-w-sm space-y-6 fade-up">
          <div>
            <Link to="/" className="flex items-center gap-2 lg:hidden mb-8">
              <div className="w-7 h-7 rounded-md bg-primary flex items-center justify-center">
                <Sparkles className="w-4 h-4 text-white" />
              </div>
              <span className="font-display font-bold text-lg">SmartStockAI</span>
            </Link>
            <h2 className="font-display text-3xl font-bold tracking-tight">Entrar</h2>
            <p className="text-sm text-muted-foreground mt-2">Acesse seu painel inteligente.</p>
          </div>
          <div className="space-y-4">
            <div>
              <Label htmlFor="email">Email</Label>
              <Input data-testid="login-email-input" id="email" type="email" required value={email} onChange={(e) => setEmail(e.target.value)} placeholder="voce@empresa.com" className="mt-2" />
            </div>
            <div>
              <Label htmlFor="password">Senha</Label>
              <Input data-testid="login-password-input" id="password" type="password" required value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="mt-2" />
            </div>
          </div>
          <Button data-testid="login-submit-btn" type="submit" disabled={loading} className="w-full h-11 bg-primary hover:bg-primary/90">
            {loading ? "Entrando..." : "Entrar"}
            <ArrowRight className="w-4 h-4 ml-2" />
          </Button>
          <p className="text-sm text-muted-foreground text-center">
            Não tem conta?{" "}
            <Link data-testid="goto-signup-link" to="/signup" className="text-primary hover:underline">Criar conta</Link>
          </p>
        </form>
      </div>
    </div>
  );
}
